import SkipUnskipIcon from '@atlaskit/icon-lab/core/skip-unskip';
import { Box, xcss } from '@atlaskit/primitives';
import { MultiValue } from '@atlaskit/react-select';
import Select, {
  ValueType,
  type OptionType,
  type GroupType,
  StylesConfig,
} from '@atlaskit/select';
import { SimpleTag as Tag } from '@atlaskit/tag';

import { getTagColor } from 'src/components/pipelines/components/test-management/utils';
import { useAnalytics } from 'src/components/settings/analytics';

import { TargetTypeEnum, TestOutcomeEnum, TestTypeEnum } from '../types';

const quarantineLabelStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
});

const statusOptions: OptionType[] = [
  { label: 'Error', value: TestOutcomeEnum.Error },
  { label: 'Failed', value: TestOutcomeEnum.Failed },
  { label: 'Skipped', value: TestOutcomeEnum.Skipped },
  { label: 'Passed', value: TestOutcomeEnum.Passed },
];

const targetTypeOptions: OptionType[] = [
  { label: 'Reference', value: TargetTypeEnum.Reference },
  { label: 'Commit', value: TargetTypeEnum.Commit },
  { label: 'Pull request', value: TargetTypeEnum['Pull request'] },
  { label: 'Unknown', value: TargetTypeEnum.Unknown },
];

const testTypeGroupedOptions: GroupType<OptionType>[] = [
  {
    label: 'Quarantine status',
    options: [{ label: 'Quarantined', value: TestTypeEnum.Quarantined }],
  },
  {
    label: 'Test state',
    options: [
      { label: 'OK', value: TestTypeEnum.Ok },
      { label: 'Flaky', value: TestTypeEnum.Flaky },
    ],
  },
];

type StaticFilterProps = {
  name: 'status' | 'targetType' | 'testType';
  placeholder: string;
  filterValue?: string;
  onFilterChange: (status?: string) => void;
  isMulti?: boolean;
};

const getSelectedOptions = (
  filterValue: string | undefined,
  options: OptionType[] | GroupType<OptionType>[]
): OptionType[] => {
  if (!filterValue) return [];
  const filterSet = new Set(filterValue.split(','));

  const flatOptions: OptionType[] = [];
  options.forEach(option => {
    if ('options' in option) {
      flatOptions.push(...option.options);
    } else {
      flatOptions.push(option);
    }
  });

  return flatOptions.filter(option => filterSet.has(option.value.toString()));
};

export const StaticFilter = ({
  name,
  placeholder,
  filterValue,
  onFilterChange,
  isMulti,
}: StaticFilterProps) => {
  let options: OptionType[] | GroupType<OptionType>[] = statusOptions;
  if (name === 'targetType') {
    options = targetTypeOptions;
  }
  if (name === 'testType') {
    options = testTypeGroupedOptions;
  }

  const { publishButtonClickEvent } = useAnalytics({
    source: 'pipelinesTestManagement',
  });

  const trackFilterAnalytics = (selectedValue: string | undefined) => {
    publishButtonClickEvent(`${name}Filter`, {
      selectedValue: selectedValue || 'cleared',
      filterType: name,
    });
  };

  const onChange = (
    selection: ValueType<OptionType, false> | MultiValue<OptionType>
  ) => {
    let selectedValue: string | undefined;
    if (!selection || (Array.isArray(selection) && selection.length === 0)) {
      selectedValue = undefined;
      onFilterChange(undefined);
      trackFilterAnalytics(selectedValue);
      return;
    }
    if (isMulti) {
      const selectedFilterOptions = selection as MultiValue<OptionType>;
      selectedValue = selectedFilterOptions
        .map(opt => opt.value.toString())
        .join(',');
      onFilterChange(selectedValue);
    } else {
      const selectedFilterOptions = selection as OptionType;
      selectedValue = selectedFilterOptions.value.toString();
      onFilterChange(selectedValue);
    }
    trackFilterAnalytics(selectedValue);
  };

  const selectedOptions = getSelectedOptions(filterValue, options);

  const formatOptionLabel = (option: OptionType) => {
    if (option.value === TestTypeEnum.Quarantined) {
      return (
        <Box xcss={quarantineLabelStyles}>
          <SkipUnskipIcon label="" color="currentColor" />
          {option.label}
        </Box>
      );
    }

    return (
      <Tag color={getTagColor(option.value as string)} text={option.label} />
    );
  };

  const customStyles: StylesConfig<any, boolean> = {
    menu: base => ({
      ...base,
      minWidth: '250px',
    }),
  };

  return (
    <Select
      isClearable
      name={name}
      isMulti={isMulti}
      value={selectedOptions}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      formatOptionLabel={formatOptionLabel}
      styles={customStyles}
    />
  );
};
